import { PlatformSdkError, formSessionError, portalFormSessionError } from '@jukun/platform-sdk/core';
import {
  canonicalFormKey, copyFormSessionData, guardFormSessionPageBridge, runFormSessionOperation,
  sameFormSessionToken, validateFormSessionSnapshot,
  type FormSessionCloseReason, type FormSessionPageBridge, type FormSessionPageContext,
  type FormSessionPageState, type FormSessionReadyReport, type FormSessionScope,
  type FormSessionSnapshot, type FormSessionToken,
} from '@jukun/platform-sdk/shell';
import type { PlatformAccessToken } from '@jukun/platform-sdk/auth';
import type { WorkflowPageFailure, WorkflowWorkItemSaveRequest, WorkflowWorkItemValidationRequest } from '@jukun/platform-sdk/workflow';
import type { PortalApplication, PortalIdentity } from '../model/portalModels';
import { ApplicationFormRuntimeRegistry, formRuntimeEntryFingerprint, type ApplicationFormRuntime, type FormRuntimeFactory } from './applicationFormRuntimeRegistry';

export const FORM_SESSION_OPEN_BUDGET_MS = 20_000;
export const FORM_SESSION_READY_BUDGET_MS = 8_000;
export const FORM_SESSION_CLOSE_BUDGET_MS = 2_000;
export const FORM_SESSION_UPDATE_BUDGET_MS = 2_000;
export type FormSelectionTarget = Readonly<{ applicationCode?: string; intentKey?: string }> & Readonly<
  | { contextType: 'WORK_ITEM' | 'DRAFT'; subjectPublicId: string; targetRef?: string }
  | { contextType: 'PROCESS_INITIATION_VIEW'; targetRef: string; subjectPublicId?: string }
>;
export type FormSelectionTicket = Readonly<{ generation: number; target: FormSelectionTarget; formKey?: string; targetKey: string; displayLease: string; signal: AbortSignal }>;
export type FormSessionOpenInput = Readonly<{ application: PortalApplication; pageRoute: string; context: FormSessionPageContext; targetRef?: string }>;
export type FormDisplayLease = Readonly<{ id: string; token: FormSessionToken; release: () => void }>;
export type FormSessionLeaveStatus = 'ALLOW' | 'CANCELLED' | 'BUSY' | 'STATE_UNAVAILABLE' | 'SUPERSEDED' | 'LEAVE_CHANGED';
/** Local navigation control flow, never a source-form failure or a user-facing error. */
export class FormLeaveCancelledError extends Error {
  constructor() { super('Form navigation cancelled'); this.name = 'FormLeaveCancelledError'; }
}
export type FormLeavePermit = Readonly<{ status: FormSessionLeaveStatus; ticket: object; token?: FormSessionToken; lease?: string; canSaveBeforeLeave: boolean; changeRevision?: number }>;
export type FormLeavePrompt = (input: Readonly<{ token: FormSessionToken; signal: AbortSignal; save: () => Promise<void> }>) => Promise<'SAVE' | 'DISCARD' | 'CANCEL'>;
export type FormSessionManagerSnapshot = Readonly<{
  latestRequest?: FormSelectionTicket;
  openingSelection?: FormSelectionTicket;
  activeSession?: Readonly<{ token: FormSessionToken; snapshot: FormSessionSnapshot; status: 'OPENING' | 'READY' | 'FAILED'; lease: string; state?: FormSessionPageState; hostPending: number; uncertain: boolean; canSaveBeforeLeave: boolean; error?: unknown }>;
  error?: unknown;
}>;
type Session = {
  token: FormSessionToken; snapshot: FormSessionSnapshot; runtime: ApplicationFormRuntime;
  ticket: FormSelectionTicket; lease: string; abort: AbortController; status: 'OPENING' | 'READY' | 'FAILED' | 'CLOSED';
  applied: boolean; terminal?: 'READY' | 'FAILED'; readyReport?: FormSessionReadyReport;
  pageState?: FormSessionPageState; hostPending: number; uncertain: boolean; saveEnabled: boolean;
  bridge?: { id: object; value: FormSessionPageBridge }; error?: unknown;
  draftUpdates: Map<number, { version: number; result: Promise<FormSessionSnapshot> }>; draftUpdateTail: Promise<unknown>;
  resolveReady: () => void; rejectReady: (error: unknown) => void; ready: Promise<void>;
};
export type FormSessionManagerOptions = {
  accessToken: (applicationCode: string, signal: AbortSignal) => Promise<PlatformAccessToken>;
  createFactory: (identity: PortalIdentity, accessToken: PlatformAccessToken) => FormRuntimeFactory;
  saveBeforeLeave?: (token: FormSessionToken) => Promise<void>;
  registry?: ApplicationFormRuntimeRegistry;
};

/** One manager for the authenticated Shell. Pages own leases, never physical instances. */
export class PortalFormSessionManager {
  readonly registry: ApplicationFormRuntimeRegistry;
  private identity?: PortalIdentity;
  private authEpoch = crypto.randomUUID();
  private applications = new Map<string, PortalApplication>();
  private nextGeneration = 0;
  private latestRequest?: FormSelectionTicket;
  private openingSelection?: FormSelectionTicket;
  private latestAbort?: AbortController;
  private activeSession?: Session;
  private leaveIntent?: { ticket: object; abort: AbortController; promise: Promise<FormLeavePermit> };
  private leavePrompt?: FormLeavePrompt;
  private saveBeforeLeave?: (token: FormSessionToken) => Promise<void>;
  private commitTail: Promise<unknown> = Promise.resolve();
  private readonly openRequests = new WeakMap<FormSelectionTicket, { fingerprint: string; promise: Promise<FormSessionToken> }>();
  private readonly runtimeFactories = new Map<ApplicationFormRuntime, FormRuntimeFactory>();
  private readonly listeners = new Set<() => void>();
  private snapshot: FormSessionManagerSnapshot = Object.freeze({});
  private error?: unknown;

  constructor(private readonly options: FormSessionManagerOptions) {
    this.registry = options.registry ?? new ApplicationFormRuntimeRegistry();
    this.saveBeforeLeave = options.saveBeforeLeave;
  }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  setLeavePrompt(prompt?: FormLeavePrompt) { this.leavePrompt = prompt; }
  setSaveBeforeLeave(save?: (token: FormSessionToken) => Promise<void>) { this.saveBeforeLeave = save; }

  setAuthenticatedContext(identity: PortalIdentity | undefined, applications: readonly PortalApplication[] = []) {
    const old = this.identity;
    const identityChanged = !identity || !old || old.actorPublicId !== identity.actorPublicId || old.tenantPublicId !== identity.tenantPublicId || old.platformOperator !== identity.platformOperator;
    if (identityChanged) {
      this.cancelIntent();
      if (this.activeSession) this.revoke(this.activeSession);
      this.activeSession = undefined;
      for (const record of this.registry.values()) {
        const factory = this.runtimeFactories.get(record);
        if (factory) this.registry.invalidate(record.applicationCode, factory);
      }
      this.authEpoch = crypto.randomUUID(); this.nextGeneration = 0;
    }
    const next = new Map(applications.map(application => [application.appCode, application]));
    for (const [code, application] of this.applications) {
      const replacement = next.get(code);
      if (!replacement || replacement.status !== 'ENABLED' || formRuntimeEntryFingerprint(application) !== formRuntimeEntryFingerprint(replacement)) this.invalidateApplication(code);
    }
    this.identity = identity; this.applications = next; this.publish();
  }

  invalidateApplication(applicationCode: string) {
    const record = this.registry.get(applicationCode);
    if (this.latestRequest?.target.applicationCode === applicationCode) this.cancelIntent();
    if (this.activeSession && this.activeSession.runtime === record) { this.revoke(this.activeSession); this.activeSession = undefined; }
    const factory = record && this.runtimeFactories.get(record);
    if (record && factory) this.registry.invalidate(applicationCode, factory);
    this.publish();
  }

  beginSelection(target: FormSelectionTarget, options: Readonly<{ force?: boolean }> = {}): FormSelectionTicket {
    const targetRef = target.targetRef ?? target.subjectPublicId;
    if (!this.identity || !targetRef || !['WORK_ITEM', 'DRAFT', 'PROCESS_INITIATION_VIEW'].includes(target.contextType)
        || (target.contextType === 'PROCESS_INITIATION_VIEW' && !target.targetRef)
        || (target.contextType !== 'PROCESS_INITIATION_VIEW' && targetRef !== target.subjectPublicId)) throw formSessionError('INPUT_INVALID');
    this.assertNotBusy();
    const formKey = target.applicationCode && target.subjectPublicId ? JSON.stringify([this.identity.tenantPublicId, target.applicationCode, target.contextType, target.subjectPublicId]) : undefined;
    const targetKey = JSON.stringify([this.identity.tenantPublicId, target.applicationCode ?? null, target.contextType, targetRef, target.subjectPublicId ?? null]);
    const sameIntent = this.latestRequest?.targetKey === targetKey && this.latestRequest.target.intentKey === target.intentKey && !this.latestRequest.signal.aborted;
    if (!options.force && sameIntent) return this.latestRequest!;
    if (this.nextGeneration >= Number.MAX_SAFE_INTEGER) throw formSessionError('INPUT_INVALID');
    this.cancelIntent();
    const abort = new AbortController();
    const ticket = Object.freeze({ generation: ++this.nextGeneration, target: Object.freeze({ ...target, targetRef }), formKey, targetKey, displayLease: crypto.randomUUID(), signal: abort.signal });
    this.latestAbort = abort; this.latestRequest = ticket; this.error = undefined;
    // Only OPENING is superseded immediately. The existing READY form stays usable until leave commits.
    const session = this.activeSession;
    if (session?.status === 'OPENING') this.revoke(session);
    this.publish();
    return ticket;
  }

  isCurrentSelection(ticket: FormSelectionTicket) { return this.latestRequest === ticket && !ticket.signal.aborted; }

  open(ticket: FormSelectionTicket, input: FormSessionOpenInput, onLeaveCommitted?: () => void): Promise<FormSessionToken> {
    const resolvedReference = input.targetRef ?? (input.context.contextType === 'PROCESS_INITIATION_VIEW' ? null : formSessionSubject(input.context));
    const fingerprint = JSON.stringify([input.application.appCode, input.pageRoute, input.context, resolvedReference]);
    const existing = this.openRequests.get(ticket);
    if (existing) { if (existing.fingerprint !== fingerprint) throw formSessionError('INPUT_INVALID'); return existing.promise; }
    const operation = this.openInternal(ticket, input, onLeaveCommitted);
    void operation.catch(() => undefined);
    this.openRequests.set(ticket, { fingerprint, promise: operation });
    return operation;
  }

  private async openInternal(ticket: FormSelectionTicket, input: FormSessionOpenInput, onLeaveCommitted?: () => void): Promise<FormSessionToken> {
    this.assertTicket(ticket);
    const formKey = canonicalFormKey(this.identity!.tenantPublicId, input.context);
    if (input.context.contextType !== ticket.target.contextType
        || (ticket.formKey && ticket.formKey !== formKey) || (ticket.target.applicationCode && input.application.appCode !== ticket.target.applicationCode)) throw formSessionError('INPUT_INVALID');
    const authorized = this.applications.get(input.application.appCode);
    if (!authorized || authorized.status !== 'ENABLED' || formRuntimeEntryFingerprint(authorized) !== formRuntimeEntryFingerprint(input.application)) throw formSessionError('INPUT_INVALID');
    const active = this.activeSession;
    // Validate the full trusted input before leave/budget; the physical epoch is assigned later.
    const validationEpoch = crypto.randomUUID();
    const validated = validateFormSessionSnapshot({ token: { runtimeEpoch: validationEpoch, formKey, generation: ticket.generation, sessionId: JSON.stringify([validationEpoch, formKey, ticket.generation]) }, revision: 1, tenantPublicId: this.identity!.tenantPublicId, pageRoute: input.pageRoute, context: input.context }, { ...this.identity!, applicationCode: input.application.appCode, basePath: input.application.entry.basePath }, validationEpoch);
    const context = validated.context;
    const subject = formSessionSubject(context);
    // Initiated detail echoes the requested card separately from its canonical process instance.
    const resolvedTargetRef = context.contextType === 'PROCESS_INITIATION_VIEW' ? input.targetRef : input.targetRef ?? subject;
    if (resolvedTargetRef !== ticket.target.targetRef || (ticket.target.subjectPublicId && ticket.target.subjectPublicId !== subject)
        || (context.contextType !== 'PROCESS_INITIATION_VIEW' && resolvedTargetRef !== subject)) throw formSessionError('INPUT_INVALID');
    if (active?.status === 'READY' && active.token.formKey === formKey && ticket.target.intentKey === active.ticket.target.intentKey
        && active.snapshot.pageRoute === validated.pageRoute && JSON.stringify(active.snapshot.context) === JSON.stringify(validated.context)) return active.token;
    const permit = await this.obtainLeavePermit(ticket, ticket.signal);
    if (permit.status !== 'ALLOW') throw this.leaveError(permit.status);
    this.assertTicket(ticket);
    // Preparation may fetch the next detail while the current form stays visible.
    // Only a granted leave permit allows the Shell to cover it with a loading state.
    this.openingSelection = ticket; this.publish();
    const deadline = Date.now() + FORM_SESSION_OPEN_BUDGET_MS;
    try {
      // Task details may render after leave commits, independently of source startup.
      // Other callers keep the existing atomic prepare-and-display behavior.
      if (onLeaveCommitted) {
        const leave = this.commitTail.catch(() => undefined).then(async () => {
          this.assertTicket(ticket);
          if (!this.permitCurrent(permit)) throw this.leaveError('LEAVE_CHANGED');
          if (this.activeSession) await this.closeSession(this.activeSession, 'REPLACED', deadline);
          this.assertTicket(ticket);
          onLeaveCommitted();
        });
        this.commitTail = leave;
        await this.wait(leave, ticket.signal, deadline);
      }
      const accessToken = await this.wait(this.options.accessToken(input.application.appCode, ticket.signal), ticket.signal, deadline);
      this.assertTicket(ticket);
      const factory = this.options.createFactory(this.identity!, accessToken);
      const creation = this.registry.ensure(input.application, this.authEpoch, factory);
      const physicalRecord = this.registry.get(input.application.appCode)!;
      this.runtimeFactories.set(physicalRecord, factory);
      physicalRecord.onReclaimed = () => { this.runtimeFactories.delete(physicalRecord); };
      const runtime = await this.wait(creation, ticket.signal, deadline);
      this.assertTicket(ticket);
      const token = Object.freeze({ runtimeEpoch: runtime.runtimeEpoch, formKey, generation: ticket.generation, sessionId: JSON.stringify([runtime.runtimeEpoch, formKey, ticket.generation]) });
      const snapshot = copyFormSessionData({ ...validated, token });
      const receiver = await this.wait(runtime.receiver ? Promise.resolve(runtime.receiver) : runtime.receiverReady, ticket.signal, deadline);
      this.assertTicket(ticket);
      const commit = this.commitTail.catch(() => undefined).then(async () => {
        this.assertTicket(ticket);
        if (onLeaveCommitted ? !!this.activeSession : !this.permitCurrent(permit)) throw this.leaveError('LEAVE_CHANGED');
        if (this.activeSession) await this.closeSession(this.activeSession, 'REPLACED', deadline);
        this.assertTicket(ticket);
        if (runtime.receiver !== receiver) throw formSessionError('HOST_UNAVAILABLE');
        const session = this.createSession(ticket, snapshot, runtime);
        this.activeSession = session;
        runtime.onReceiverLost = () => { if (this.activeSession?.runtime === runtime) this.fail(this.activeSession, formSessionError('HOST_UNAVAILABLE'), true); };
        this.publish();
        const applied = await runFormSessionOperation(operation => receiver.activate(snapshot, this.createScope(session), operation), { signal: AbortSignal.any([ticket.signal, session.abort.signal]), remainingMs: deadline - Date.now() }, 'OPEN_TIMEOUT');
        this.assertSession(session); this.assertTicket(ticket);
        if (applied.status !== 'APPLIED' || !sameFormSessionToken(applied.token, token)) throw formSessionError('APPLY_FAILED');
        session.applied = true; this.commitReady(session);
        await this.wait(session.ready, session.abort.signal, Math.min(deadline, Date.now() + FORM_SESSION_READY_BUDGET_MS));
        this.assertSession(session); this.assertTicket(ticket);
        return token;
      });
      this.commitTail = commit;
      void commit.catch(() => undefined);
      return await this.wait(commit, ticket.signal, deadline);
    } catch (cause) {
      const session = this.activeSession;
      if (session?.ticket === ticket && session.status === 'OPENING') this.fail(session, cause, true);
      if (this.isCurrentSelection(ticket)) { this.error = cause; this.publish(); }
      throw cause;
    }
  }

  acquireDisplayLease(token: FormSessionToken, target: HTMLElement): FormDisplayLease {
    const session = this.requireSession(token, false);
    const id = crypto.randomUUID(); session.lease = id;
    this.registry.attach(session.runtime, target, id); this.publish();
    return Object.freeze({ id, token, release: () => {
      // Release only this display position, not the logical session. Park before
      // React removes the host, including StrictMode/failed-session cleanup.
      // An old session or host must never reclaim a newer display lease.
      if (session.lease !== id || session.runtime.displayLease !== id) return;
      this.registry.park(session.runtime, id);
      session.lease = crypto.randomUUID(); this.publish();
    } });
  }

  setSaveEligibility(token: FormSessionToken, enabled: boolean) {
    const session = this.requireSession(token, false); session.saveEnabled = enabled; this.publish();
  }

  getBridge(token: FormSessionToken): FormSessionPageBridge | undefined { return this.requireSession(token, true).bridge?.value; }
  getSessionSnapshot(token: FormSessionToken): FormSessionSnapshot { return this.requireSession(token, true).snapshot; }

  async runHostOperation<T>(token: FormSessionToken, operation: (snapshot: FormSessionSnapshot, signal: AbortSignal) => Promise<T>, options: Readonly<{ allowDraftRevisionUpdate?: boolean }> = {}): Promise<T> {
    const session = this.requireSession(token, true);
    const snapshot = session.snapshot; session.hostPending += 1; this.publish();
    try {
      const result = await operation(snapshot, session.abort.signal);
      this.requireSession(token, true);
      if (session.snapshot.revision !== snapshot.revision && !(options.allowDraftRevisionUpdate && snapshot.context.contextType === 'DRAFT')) throw formSessionError('STALE');
      return result;
    } finally { session.hostPending = Math.max(0, session.hostPending - 1); this.publish(); }
  }
  setUncertain(token: FormSessionToken, uncertain: boolean) { const session = this.requireSession(token, false); session.uncertain = uncertain; this.publish(); }

  updateDraft(token: FormSessionToken, expectedDraftVersion: number, revision?: number): Promise<FormSessionSnapshot> {
    const session = this.requireSession(token, true);
    const snapshot = session.snapshot;
    if (snapshot.context.contextType !== 'DRAFT' || !Number.isSafeInteger(expectedDraftVersion)
        || expectedDraftVersion < 1) throw formSessionError('UPDATE_INVALID');
    const nextRevision = revision ?? snapshot.revision + 1;
    if (!Number.isSafeInteger(nextRevision) || nextRevision < 1) throw formSessionError('UPDATE_INVALID');
    const replay = session.draftUpdates.get(nextRevision);
    if (replay) {
      if (replay.version !== expectedDraftVersion) throw formSessionError('UPDATE_INVALID');
      return replay.result;
    }
    if (nextRevision <= snapshot.revision) {
      if (expectedDraftVersion > snapshot.context.draftContext.expectedDraftVersion) throw formSessionError('UPDATE_INVALID');
      return Promise.resolve(snapshot);
    }
    if (expectedDraftVersion < snapshot.context.draftContext.expectedDraftVersion) throw formSessionError('UPDATE_INVALID');
    const deadline = Date.now() + FORM_SESSION_UPDATE_BUDGET_MS;
    session.hostPending += 1; this.publish();
    const previousUpdate = session.draftUpdateTail;
    const result = runFormSessionOperation(async operation => {
      await previousUpdate.catch(() => undefined);
      this.requireSession(token, true);
      const current = session.snapshot;
      if (current.context.contextType !== 'DRAFT' || nextRevision <= current.revision
          || expectedDraftVersion < current.context.draftContext.expectedDraftVersion) throw formSessionError('UPDATE_INVALID');
      const receiver = session.runtime.receiver;
      if (!receiver) throw formSessionError('HOST_UNAVAILABLE');
      const receipt = await receiver.update({ token, revision: nextRevision, expectedDraftVersion },
        { signal: operation.signal, remainingMs: deadline - Date.now() });
      this.assertSession(session);
      if (operation.signal.aborted) throw formSessionError('UPDATE_TIMEOUT');
      if (receipt.status !== 'APPLIED' || receipt.revision !== nextRevision || !sameFormSessionToken(receipt.token, token)) throw formSessionError('UPDATE_INVALID');
      session.snapshot = copyFormSessionData({ ...current, revision: nextRevision,
        context: { ...current.context, draftContext: { ...current.context.draftContext, expectedDraftVersion } } });
      this.publish(); return session.snapshot;
    }, { signal: session.abort.signal, remainingMs: FORM_SESSION_UPDATE_BUDGET_MS }, 'UPDATE_TIMEOUT')
      .catch(cause => { this.fail(session, cause, true); throw cause; })
      .finally(() => { session.hostPending = Math.max(0, session.hostPending - 1); this.publish(); });
    void result.catch(() => undefined);
    session.draftUpdates.set(nextRevision, { version: expectedDraftVersion, result });
    session.draftUpdateTail = result;
    return result;
  }

  requestLeave(target: string): Promise<FormLeavePermit> {
    this.cancelIntent();
    const ticket = Object.freeze({ target, id: crypto.randomUUID() });
    const abort = new AbortController();
    const promise = this.obtainLeavePermit(ticket, abort.signal);
    void promise.catch(() => undefined);
    this.leaveIntent = { ticket, abort, promise };
    return promise;
  }
  isLeavePermitCurrent(permit: FormLeavePermit) { return permit.status === 'ALLOW' && this.leaveIntent?.ticket === permit.ticket && this.permitCurrent(permit); }
  async commitLeave(permit: FormLeavePermit): Promise<FormSessionLeaveStatus> {
    if (!this.isLeavePermitCurrent(permit)) return 'LEAVE_CHANGED';
    if (this.activeSession) await this.closeSession(this.activeSession, 'PAGE_LEFT', Date.now() + FORM_SESSION_CLOSE_BUDGET_MS);
    if (this.leaveIntent?.ticket !== permit.ticket) return 'SUPERSEDED';
    this.leaveIntent = undefined; this.publish(); return 'ALLOW';
  }
  shouldBlockBeforeUnload() { const session = this.activeSession; return !!session && this.canSave(session) && session.pageState?.dirty === true; }

  private async obtainLeavePermit(ticket: object, signal: AbortSignal): Promise<FormLeavePermit> {
    const session = this.activeSession;
    const currentIntent = () => !signal.aborted && (this.latestRequest === ticket || this.leaveIntent?.ticket === ticket);
    const make = (status: FormSessionLeaveStatus): FormLeavePermit => Object.freeze({ status, ticket, token: session?.token, lease: session?.lease, canSaveBeforeLeave: session ? this.canSave(session) : false, changeRevision: session && this.canSave(session) ? session.pageState?.changeRevision : undefined });
    // requestLeave installs its intent before this function resumes from any await.
    if (!session || session.status === 'CLOSED') return make('ALLOW');
    if (this.isBusy(session)) return make('BUSY');
    const permit = make('ALLOW');
    if (!permit.canSaveBeforeLeave) return permit;
    if (!session.pageState) return make('STATE_UNAVAILABLE');
    if (!session.pageState.dirty) return permit;
    if (!this.leavePrompt || !this.saveBeforeLeave) return make('STATE_UNAVAILABLE');
    let savePromise: Promise<void> | undefined;
    const save = () => {
      if (!savePromise) { savePromise = this.saveBeforeLeave!(session.token); void savePromise.catch(() => undefined); }
      return savePromise;
    };
    const choice = await this.leavePrompt({ token: session.token, signal, save });
    if (!currentIntent()) return make('SUPERSEDED');
    if (choice === 'CANCEL') return make('CANCELLED');
    if (!this.permitCurrent(permit)) return make('LEAVE_CHANGED');
    if (choice === 'SAVE') {
      await save();
      if (!currentIntent()) return make('SUPERSEDED');
      const savedState = this.getSnapshot().activeSession?.state;
      if (this.activeSession !== session || this.isBusy(session) || this.canSave(session) !== permit.canSaveBeforeLeave
          || savedState?.dirty !== false || savedState.changeRevision !== permit.changeRevision) return make('LEAVE_CHANGED');
    }
    return permit;
  }

  private createSession(ticket: FormSelectionTicket, snapshot: FormSessionSnapshot, runtime: ApplicationFormRuntime): Session {
    let resolveReady!: () => void; let rejectReady!: (error: unknown) => void;
    const ready = new Promise<void>((resolve, reject) => { resolveReady = resolve; rejectReady = reject; });
    void ready.catch(() => undefined);
    return { token: snapshot.token, snapshot, runtime, ticket, lease: ticket.displayLease, abort: new AbortController(), status: 'OPENING', applied: false, hostPending: 0, uncertain: false, saveEnabled: true, draftUpdates: new Map(), draftUpdateTail: Promise.resolve(), ready, resolveReady, rejectReady };
  }

  private createScope(session: Session): FormSessionScope {
    const accepted = (): boolean => this.activeSession === session && !session.abort.signal.aborted && session.status !== 'CLOSED'
      && session.runtime.runtimeEpoch === session.token.runtimeEpoch && !session.runtime.invalidated && !!session.lease
      && (session.status !== 'OPENING' || this.isCurrentSelection(session.ticket));
    const mismatch = (): never => { const error = formSessionError('CAPABILITY_MISMATCH'); this.fail(session, error); throw error; };
    return Object.freeze({
      token: session.token, signal: session.abort.signal,
      registerPageBridge: value => {
        if (!accepted()) throw formSessionError('STALE');
        if (session.bridge) throw formSessionError('BRIDGE_DUPLICATE');
        if (session.status === 'FAILED' || session.terminal === 'FAILED') throw formSessionError('STALE');
        if (session.terminal === 'READY') return mismatch();
        const id = {};
        let guarded: FormSessionPageBridge;
        try { guarded = guardFormSessionPageBridge(value, { signal: session.abort.signal, isCurrent: () => accepted() && session.bridge?.id === id, isReady: () => session.status === 'READY', getSnapshot: () => session.snapshot }); }
        catch { return mismatch(); }
        const runBridge = async <T,>(write: boolean, operation: () => Promise<T>): Promise<T> => {
          try { return await this.runHostOperation(session.token, () => operation()); }
          catch (error) {
            if (write && error instanceof PlatformSdkError && /TIMEOUT/.test(error.code) && this.activeSession === session) {
              session.uncertain = true; this.publish();
            }
            throw error;
          }
        };
        const valueWithPending: FormSessionPageBridge = guarded.contextType === 'WORK_ITEM'
          ? Object.freeze({ contextType: 'WORK_ITEM', bridge: Object.freeze({
            validate: (request: WorkflowWorkItemValidationRequest, options?: Readonly<{ signal?: AbortSignal }>) => runBridge(false, () => guarded.bridge.validate(request, options)),
            save: (request: WorkflowWorkItemSaveRequest, options?: Readonly<{ signal?: AbortSignal }>) => runBridge(true, () => guarded.bridge.save(request, options)),
          }) })
          : Object.freeze({ contextType: 'DRAFT', bridge: Object.freeze({
            save: () => runBridge(true, () => guarded.bridge.save()),
            prepareSubmit: () => runBridge(true, () => guarded.bridge.prepareSubmit()),
          }) });
        session.bridge = { id, value: valueWithPending }; this.publish();
        return () => { if (session.bridge?.id !== id) return; session.bridge = undefined; if (accepted() && session.terminal === 'READY') this.fail(session, formSessionError('CAPABILITY_MISMATCH')); this.publish(); };
      },
      reportReady: report => {
        if (!accepted()) return 'IGNORED_STALE';
        const context = session.snapshot.context;
        const readOnly = context.contextType === 'PROCESS_INITIATION_VIEW' || (context.contextType === 'WORK_ITEM' && context.recipientType !== 'TODO');
        if (!report || Object.keys(report).length !== 3 || report.pageRoute !== session.snapshot.pageRoute || report.contextType !== context.contextType
            || !['REQUIRED', 'NONE'].includes(report.saveCapability)
            || (context.contextType === 'DRAFT' && report.saveCapability !== 'REQUIRED')
            || (readOnly && report.saveCapability !== 'NONE')) return mismatch();
        if (session.terminal) return 'IGNORED_DUPLICATE';
        if ((!readOnly && !session.pageState)
            || (report.saveCapability === 'REQUIRED' && (!session.bridge || !session.pageState))
            || (report.saveCapability === 'NONE' && !!session.bridge)
            || (readOnly && session.pageState?.dirty)) return mismatch();
        session.readyReport = Object.freeze({ ...report }); session.terminal = 'READY'; this.commitReady(session); this.publish(); return 'ACCEPTED';
      },
      reportFailed: (failure: WorkflowPageFailure) => {
        if (!accepted()) return 'IGNORED_STALE';
        if (!failure || Object.keys(failure).length !== 4 || failure.pageRoute !== session.snapshot.pageRoute || !/^[A-Z][A-Z0-9_]{2,63}$/.test(failure.code)
            || typeof failure.message !== 'string' || failure.message.length < 1 || failure.message.length > 300 || failure.message !== failure.message.trim() || /[\r\n]/.test(failure.message) || typeof failure.retryable !== 'boolean') return mismatch();
        if (session.terminal) return 'IGNORED_DUPLICATE';
        session.terminal = 'FAILED'; this.fail(session, new PlatformSdkError(failure.code, '来源表单初始化失败', { retryable: failure.retryable })); return 'ACCEPTED';
      },
      reportState: value => {
        if (!accepted()) return 'IGNORED_STALE';
        if (!value || Object.keys(value).length !== 4 || !Number.isSafeInteger(value.changeRevision) || value.changeRevision < 0 || ['dirty', 'busy', 'uncertain'].some(key => typeof value[key as keyof FormSessionPageState] !== 'boolean')) return mismatch();
        const context = session.snapshot.context;
        if ((context.contextType === 'PROCESS_INITIATION_VIEW' || (context.contextType === 'WORK_ITEM' && context.recipientType !== 'TODO')) && (value.dirty || value.changeRevision !== 0)) return mismatch();
        if (session.pageState && value.changeRevision < session.pageState.changeRevision) return 'IGNORED_STALE';
        if (session.pageState && JSON.stringify(value) === JSON.stringify(session.pageState)) return 'IGNORED_DUPLICATE';
        session.pageState = Object.freeze({ ...value }); this.publish(); return 'ACCEPTED';
      },
    });
  }

  private commitReady(session: Session) {
    if (session.applied && session.terminal === 'READY' && session.status === 'OPENING') { session.status = 'READY'; session.resolveReady(); this.publish(); }
  }
  private fail(session: Session, error: unknown, revoke = false) {
    if (session.status === 'CLOSED') return;
    session.terminal ??= 'FAILED';
    session.status = 'FAILED'; session.error = error; session.rejectReady(error);
    if (revoke) session.abort.abort();
    session.bridge = undefined; this.publish();
  }
  private revoke(session: Session) {
    session.abort.abort(); session.bridge = undefined; session.status = 'CLOSED'; session.rejectReady(formSessionError('STALE'));
  }
  private async closeSession(session: Session, reason: FormSessionCloseReason, deadline: number) {
    const receiver = session.runtime.receiver;
    this.revoke(session); this.publish();
    const cleanup = new AbortController();
    try {
      if (!receiver) throw formSessionError('HOST_UNAVAILABLE');
      const result = await runFormSessionOperation(operation => receiver.deactivate(session.token, reason, operation), { signal: cleanup.signal, remainingMs: Math.min(FORM_SESSION_CLOSE_BUDGET_MS, deadline - Date.now()) }, 'CLOSE_TIMEOUT');
      if (!sameFormSessionToken(result.token, session.token) || !['RELEASED', 'ALREADY_RELEASED'].includes(result.status)) throw formSessionError('CLOSE_TIMEOUT');
      this.registry.park(session.runtime);
      if (this.activeSession === session) this.activeSession = undefined;
    } catch (cause) { this.registry.block(session.runtime); this.error = cause; throw cause; }
    finally { this.publish(); }
  }
  private canSave(session: Session) {
    if (session.status !== 'READY' || !session.bridge || !session.saveEnabled) return false;
    const context = session.snapshot.context;
    return context.contextType === 'DRAFT' ? context.availableActions.some(action => action.actionCode === 'SAVE' && action.operationType === 'SHELL_LOCAL')
      : context.contextType === 'WORK_ITEM' && context.recipientType === 'TODO' && context.allowedActions.some(action => action.actionCode === 'SAVE' && action.operationType === 'SHELL_LOCAL' && action.formValidationPolicy === 'SKIP');
  }
  private isBusy(session: Session) { return session.hostPending > 0 || session.uncertain || session.pageState?.busy === true || session.pageState?.uncertain === true; }
  private assertNotBusy() { if (this.activeSession && this.isBusy(this.activeSession)) throw this.leaveError('BUSY'); }
  private permitCurrent(permit: FormLeavePermit) {
    const session = this.activeSession;
    if (!permit.token) return !session || session.status === 'CLOSED';
    return !!session && sameFormSessionToken(session.token, permit.token) && session.lease === permit.lease && !this.isBusy(session)
      && this.canSave(session) === permit.canSaveBeforeLeave && (!permit.canSaveBeforeLeave || session.pageState?.changeRevision === permit.changeRevision);
  }
  private cancelIntent() { this.openingSelection = undefined; this.latestAbort?.abort(); this.latestAbort = undefined; this.latestRequest = undefined; this.leaveIntent?.abort.abort(); this.leaveIntent = undefined; }
  private assertTicket(ticket: FormSelectionTicket) { if (!this.isCurrentSelection(ticket)) throw formSessionError('STALE'); }
  private assertSession(session: Session) { if (this.activeSession !== session || session.abort.signal.aborted || session.status === 'CLOSED' || session.runtime.invalidated) throw formSessionError('STALE'); }
  private requireSession(token: FormSessionToken, ready: boolean) {
    const session = this.activeSession; if (!session || !sameFormSessionToken(session.token, token)) throw formSessionError('STALE');
    this.assertSession(session); if (ready && session.status !== 'READY') throw formSessionError('NOT_READY'); return session;
  }
  private leaveError(status: FormSessionLeaveStatus) {
    if (status === 'CANCELLED' || status === 'SUPERSEDED') return new FormLeaveCancelledError();
    return portalFormSessionError(status === 'BUSY' ? 'PORTAL_FORM_SESSION_SWITCH_BUSY' : 'PORTAL_FORM_SESSION_LEAVE_CHANGED');
  }
  private wait<T>(promise: Promise<T>, signal: AbortSignal, deadline: number): Promise<T> {
    void promise.catch(() => undefined);
    return runFormSessionOperation(() => promise, { signal, remainingMs: deadline - Date.now() }, 'OPEN_TIMEOUT');
  }
  private publish() {
    const session = this.activeSession;
    this.snapshot = Object.freeze({ latestRequest: this.latestRequest, openingSelection: this.openingSelection, error: this.error, activeSession: session && session.status !== 'CLOSED' ? Object.freeze({ token: session.token, snapshot: session.snapshot, status: session.status, lease: session.lease, state: session.pageState, hostPending: session.hostPending, uncertain: session.uncertain || session.pageState?.uncertain === true, canSaveBeforeLeave: this.canSave(session), error: session.error }) : undefined });
    this.listeners.forEach(listener => listener());
  }
}

function formSessionSubject(context: FormSessionPageContext): string {
  return context.contextType === 'WORK_ITEM' ? context.workflowTaskPublicId
    : context.contextType === 'DRAFT' ? context.draftContext.draftPublicId : context.processInstancePublicId;
}
